import { Facebook, Instagram, Twitter, Mail, Heart, Shield, FileText, HelpCircle, Users, Trash2 } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface FooterProps {
    onNavigate: (view: any) => void;
}

export function Footer({ onNavigate }: FooterProps) {
    const { t, currentLang } = useTranslation();
    const currentYear = new Date().getFullYear();

    const getMascotImage = (lang: string) => {
        const mascots: Record<string, string> = {
            'pt-BR': '/Brasil.webp',
            'en-US': '/Estados Unidos.webp',
            'es-ES': '/Espanha.webp',
            'de-DE': '/Alemanha.webp',
            'fr-FR': '/França.webp',
            'it-IT': '/Italia.webp',
            'zh-CN': '/china.webp',
            'ko-KR': '/Coreia_do_Sul.webp',
            'ja-JP': '/Japão.webp',
        };
        return mascots[lang] || '/Mascote.webp';
    };

    const mascotImage = getMascotImage(currentLang);

    return (
        <footer className="bg-gray-900 text-gray-300 pt-16 pb-8 mt-auto">
            <div className="container mx-auto px-4 max-w-6xl">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                    {/* Brand Section */}
                    <div>
                        <div className="flex items-center gap-2 mb-6">
                            <img
                                src={mascotImage}
                                alt="Mystery Gift"
                                className="w-10 h-10 object-contain"
                            />
                            <span className="text-2xl font-bold text-white">
                                {t('footerNav.brand.title')}
                            </span>
                        </div>
                        <p className="text-gray-400 mb-6 leading-relaxed">
                            {t('footerNav.brand.description')}
                        </p>

                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">{t('footerNav.quickNav.title')}</h3>
                        <ul className="space-y-4">
                            <li>
                                <button onClick={() => onNavigate('home')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    {t('footerNav.quickNav.home')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('blog')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    {t('footerNav.quickNav.blog')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('faq')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    {t('footerNav.quickNav.howItWorks')}
                                </button>
                            </li>

                        </ul>
                    </div>

                    {/* Legal & Support */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">{t('footerNav.legal.title')}</h3>
                        <ul className="space-y-4">
                            <li>
                                <button onClick={() => onNavigate('privacy')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <Shield className="w-4 h-4" />
                                    {t('footerNav.legal.privacy')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('terms')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    {t('footerNav.legal.terms')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('faq')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <HelpCircle className="w-4 h-4" />
                                    {t('footerNav.legal.faq')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('contact')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    {t('footerNav.legal.contact')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('about')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    {t('footerNav.legal.about')}
                                </button>
                            </li>
                            <li>
                                <button onClick={() => onNavigate('account-deletion')} className="hover:text-purple-400 transition-colors flex items-center gap-2">
                                    <Trash2 className="w-4 h-4" />
                                    {t('footerNav.legal.accountDeletion')}
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Newsletter / Contact */}
                    <div>
                        <h3 className="text-white font-bold text-lg mb-6">{t('footerNav.newsletter.title')}</h3>
                        <p className="text-gray-400 mb-4">
                            {t('footerNav.newsletter.description')}
                        </p>
                        <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="email"
                                placeholder={t('footerNav.newsletter.placeholder')}
                                className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-purple-500 transition-colors"
                            />
                            <button className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold transition-colors">
                                {t('footerNav.newsletter.button')}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="border-t border-gray-800 pt-8 mt-8 text-center">
                    <p className="text-white text-base">
                        {t('footer.rights').replace('2025', currentYear.toString())}
                    </p>
                </div>
            </div>
        </footer>
    );
}
